/**
 * Reptilian Agent - Sound System
 * Web Audio API based (Toss WebView compatible)
 * All SFX generated via oscillator (no external audio files)
 */

type SfxType =
  | 'tap'
  | 'purchase'
  | 'prestige'
  | 'achievement'
  | 'goldenEvent'
  | 'comboUp'
  | 'error'
  | 'upgrade'
  | 'boost'
  | 'conquest';

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let bgmGain: GainNode | null = null;
let sfxGain: GainNode | null = null;

let bgmVolume = 50;
let sfxVolume = 70;
let bgmPlaying = false;
let bgmSource: AudioBufferSourceNode | null = null;
let bgmPauseOffset = 0;
let bgmStartTime = 0;

const SAVE_KEY = 'reptilian-agent-sound';

// === Init ===

function initAudio(): boolean {
  if (audioCtx) return true;
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    audioCtx = new AC();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 1.0;
    masterGain.connect(audioCtx.destination);

    bgmGain = audioCtx.createGain();
    bgmGain.gain.value = bgmVolume / 100 * 0.3;
    bgmGain.connect(masterGain);

    sfxGain = audioCtx.createGain();
    sfxGain.gain.value = sfxVolume / 100;
    sfxGain.connect(masterGain);

    return true;
  } catch {
    return false;
  }
}

function resumeCtx() {
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
}

// === Settings ===

function loadSoundSettings() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      bgmVolume = s.bgmVol ?? 50;
      sfxVolume = s.sfxVol ?? 70;
    }
  } catch { /* ignore */ }
}

function saveSoundSettings() {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({ bgmVol: bgmVolume, sfxVol: sfxVolume }));
  } catch { /* ignore */ }
}

// === SFX (oscillator-based) ===

function playNote(
  freq: number, duration: number, type: OscillatorType = 'sine',
  vol = 0.15, startDelay = 0
) {
  if (!audioCtx || !sfxGain || sfxVolume <= 0) return;
  resumeCtx();

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain);
  gain.connect(sfxGain);

  osc.type = type;
  const t = audioCtx.currentTime + startDelay;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(vol, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.start(t);
  osc.stop(t + duration);
}

export function playSfx(type: SfxType) {
  if (sfxVolume <= 0) return;
  if (!initAudio()) return;

  switch (type) {
    case 'tap':
      playNote(400, 0.04, 'square', 0.06);
      playNote(800, 0.06, 'sine', 0.1);
      playNote(150, 0.05, 'triangle', 0.12);
      break;

    case 'purchase':
      playNote(1200, 0.08, 'sine', 0.12);
      playNote(1600, 0.12, 'sine', 0.1, 0.06);
      break;

    case 'prestige':
      playNote(392, 0.15, 'sine', 0.15, 0);
      playNote(523, 0.15, 'sine', 0.15, 0.12);
      playNote(659, 0.15, 'sine', 0.15, 0.24);
      playNote(784, 0.2, 'sine', 0.18, 0.36);
      playNote(1047, 0.3, 'sine', 0.12, 0.48);
      break;

    case 'achievement':
      playNote(523, 0.15, 'sine', 0.15, 0);
      playNote(659, 0.15, 'sine', 0.15, 0.08);
      playNote(784, 0.2, 'sine', 0.18, 0.16);
      playNote(1568, 0.1, 'sine', 0.06, 0.28);
      break;

    case 'goldenEvent':
      playNote(880, 0.1, 'triangle', 0.12);
      playNote(1320, 0.15, 'triangle', 0.12, 0.05);
      playNote(1760, 0.12, 'triangle', 0.1, 0.12);
      break;

    case 'comboUp':
      playNote(660, 0.06, 'triangle', 0.08);
      playNote(880, 0.08, 'triangle', 0.06, 0.04);
      break;

    case 'error':
      playNote(200, 0.2, 'square', 0.06);
      break;

    case 'upgrade':
      playNote(440, 0.08, 'sine', 0.1);
      playNote(660, 0.1, 'sine', 0.08, 0.06);
      break;

    case 'boost':
      playNote(523, 0.1, 'sine', 0.12, 0);
      playNote(784, 0.12, 'sine', 0.1, 0.06);
      playNote(1047, 0.15, 'sine', 0.12, 0.12);
      break;

    case 'conquest':
      // Triumphant fanfare for country conquest
      playNote(392, 0.15, 'sine', 0.2, 0);
      playNote(523, 0.15, 'sine', 0.2, 0.1);
      playNote(659, 0.15, 'sine', 0.2, 0.2);
      playNote(784, 0.2, 'sine', 0.25, 0.3);
      playNote(1047, 0.25, 'sine', 0.2, 0.45);
      playNote(1568, 0.3, 'triangle', 0.15, 0.6);
      break;
  }
}

// === BGM (procedural ambient drone) ===

let bgmBuffer: AudioBuffer | null = null;

function generateBgmBuffer(): AudioBuffer | null {
  if (!audioCtx) return null;
  // Generate a 10-second ambient drone loop
  const sampleRate = audioCtx.sampleRate;
  const duration = 10;
  const length = sampleRate * duration;
  const buffer = audioCtx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    // Low drone
    const drone = Math.sin(2 * Math.PI * 55 * t) * 0.15;
    // Subtle overtone
    const overtone = Math.sin(2 * Math.PI * 82.5 * t) * 0.08;
    // Very subtle high shimmer
    const shimmer = Math.sin(2 * Math.PI * 220 * t + Math.sin(2 * Math.PI * 0.1 * t) * 2) * 0.03;
    // LFO modulation
    const lfo = 0.8 + 0.2 * Math.sin(2 * Math.PI * 0.15 * t);

    data[i] = (drone + overtone + shimmer) * lfo;
  }

  return buffer;
}

function startBgm() {
  if (bgmPlaying) return;
  if (!initAudio()) return;
  if (!audioCtx || !bgmGain) return;
  resumeCtx();

  if (!bgmBuffer) {
    bgmBuffer = generateBgmBuffer();
  }
  if (!bgmBuffer) return;

  bgmSource = audioCtx.createBufferSource();
  bgmSource.buffer = bgmBuffer;
  bgmSource.loop = true;
  bgmSource.connect(bgmGain);
  bgmSource.start(0, bgmPauseOffset % bgmBuffer.duration);
  bgmStartTime = audioCtx.currentTime;
  bgmPlaying = true;
}

function stopBgm() {
  if (bgmSource && audioCtx) {
    bgmPauseOffset += audioCtx.currentTime - bgmStartTime;
    try { bgmSource.stop(); } catch { /* ignore */ }
    bgmSource = null;
  }
  bgmPlaying = false;
}

// === Public API ===

export function initSound() {
  loadSoundSettings();

  if (initAudio()) {
    bgmBuffer = generateBgmBuffer();
  }

  const startOnInteraction = () => {
    if (!initAudio()) return;
    resumeCtx();
    if (bgmVolume > 0 && !bgmPlaying) {
      startBgm();
    }
  };

  ['touchstart', 'click', 'pointerdown'].forEach(evt => {
    document.addEventListener(evt, startOnInteraction, { once: true, passive: true });
  });

  // Background handling
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (bgmPlaying) stopBgm();
    } else {
      if (bgmVolume > 0) startBgm();
    }
  });
}

export function getBgmVolume(): number { return bgmVolume; }
export function getSfxVolume(): number { return sfxVolume; }

export function setBgmVolume(vol: number) {
  bgmVolume = Math.max(0, Math.min(100, Math.round(vol)));
  if (bgmGain) bgmGain.gain.value = bgmVolume / 100 * 0.3;
  if (bgmVolume > 0 && !bgmPlaying) startBgm();
  else if (bgmVolume <= 0 && bgmPlaying) stopBgm();
  saveSoundSettings();
}

export function setSfxVolume(vol: number) {
  sfxVolume = Math.max(0, Math.min(100, Math.round(vol)));
  if (sfxGain) sfxGain.gain.value = sfxVolume / 100;
  saveSoundSettings();
}

export function isBgmEnabled(): boolean { return bgmVolume > 0; }
export function isSfxEnabled(): boolean { return sfxVolume > 0; }
